import { useCallback, useEffect, useState } from 'preact/hooks'
import { outboundWebhooksApi, type DeliveryQuery } from '@/api/outboundWebhooks'
import type {
    OutboundWebhookSubscriptionViewDto,
    OutboundWebhookDeliveryViewDto,
    OutboundWebhookDeliveryCountsDto,
} from '@/types/outboundWebhooks'

const EMPTY_COUNTS: OutboundWebhookDeliveryCountsDto = {
    Pending: 0, Delivered: 0, Failed: 0, Cancelled: 0,
}

/**
 * Destinations for a company. Owns the list and reloading it; mutations live in the components that
 * trigger them so each can surface its own errors and confirmations rather than routing every
 * failure through one shared banner.
 */
export function useOutboundWebhookSubscriptions(clientCompanyId: string) {
    const [subscriptions, setSubscriptions] = useState<OutboundWebhookSubscriptionViewDto[] | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)

    const load = useCallback(async () => {
        setLoading(true)
        const response = await outboundWebhooksApi.getSubscriptions(clientCompanyId)
        if (response.error) {
            setError(response.error)
            // Deliberately not clearing the existing list: a failed refresh should not blank a
            // screen the user is reading. The banner tells them it is stale.
        } else {
            setSubscriptions(response.data ?? [])
            setError(null)
        }
        setLoading(false)
    }, [clientCompanyId])

    useEffect(() => { void load() }, [load])

    return { subscriptions, loading, error, reload: load }
}

/**
 * The delivery log for one destination. Refetches whenever the destination or the filters change.
 *
 * Counts come back unfiltered by status on purpose — the header shows the whole picture while the
 * list below it is narrowed, so someone reading delivered rows can still see failures exist.
 */
export function useOutboundWebhookDeliveries(clientCompanyId: string, query: DeliveryQuery) {
    const [deliveries, setDeliveries] = useState<OutboundWebhookDeliveryViewDto[] | null>(null)
    const [counts, setCounts] = useState<OutboundWebhookDeliveryCountsDto>(EMPTY_COUNTS)
    const [error, setError] = useState<string | null>(null)
    const [loading, setLoading] = useState(true)

    const { subscriptionId, status, workspaceId, limit } = query

    const load = useCallback(async () => {
        if (!subscriptionId) {
            setDeliveries([])
            setCounts(EMPTY_COUNTS)
            // Clearing the error too: without this a failed load keeps its banner on screen after the
            // destination it belonged to is deleted or deselected.
            setError(null)
            setLoading(false)
            return
        }
        setLoading(true)
        const response = await outboundWebhooksApi.getDeliveries(clientCompanyId, {
            subscriptionId, status, workspaceId, limit,
        })
        if (response.error) {
            setError(response.error)
        } else {
            setDeliveries(response.data?.Deliveries ?? [])
            setCounts(response.data?.Counts ?? EMPTY_COUNTS)
            setError(null)
        }
        setLoading(false)
        // Primitive deps rather than the query object: a new object literal every render would
        // refetch the log on every keystroke elsewhere in the panel.
    }, [clientCompanyId, subscriptionId, status, workspaceId, limit])

    useEffect(() => { void load() }, [load])

    return { deliveries, counts, loading, error, reload: load }
}
